/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { TextField, Checkbox, FormControlLabel, Select, MenuItem, FormControl } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { EventsCouncilFeedLinkSelector } from "./link-selector-wrapper";
import SimpliCityAdminSDK from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";

const COUNCIL_MEETING_TYPES_LIST_KEY = "council_meeting_category_list";

const EventsCouncilFeedComponentEditor = () => {
    const { component, update } = useUpdateComponent();
    const { token } = useAuth();

    const eventHeading = component?.eventHeading || "";
    const allEventsLink = component?.allEventsLink;
    const showEventDescriptions = component?.showEventDescriptions || false;
    const councilMeetingHeading = component?.councilMeetingHeading || "";
    const councilMeetingType = component?.councilMeetingType || "";
    const allCouncilMeetingsLink = component?.allCouncilMeetingsLink;

    const [councilMeetingTypeOptions, setCouncilMeetingTypeOptions] = useState([]);

    useEffect(() => {
        const fetchCouncilMeetingTypeOptions = async () => {
            try {
                const response = await SimpliCityAdminSDK.list.findOneByKey(token, COUNCIL_MEETING_TYPES_LIST_KEY);
                const options = [];
                
                // Handle different list types: list.text (array of strings) or list.entity/list.object (array of objects)
                if (response && Array.isArray(response)) {
                    for (const item of response) {
                        if (typeof item === "string") {
                            // list.text type - item is a string
                            options.push({
                                value: item,
                                label: item
                            });
                        } else if (item && typeof item === "object") {
                            // list.entity or list.object type - item has label/value structure
                            options.push({
                                value: item.value || item.label || item,
                                label: item.label || item.value || item
                            });
                        }
                    }
                }
                
                setCouncilMeetingTypeOptions(options);
            } catch (error) {
                console.error("Error fetching council meeting types:", error);
                // Fallback to empty array or default options if fetch fails
                setCouncilMeetingTypeOptions([]);
            }
        };
        
        if (token) {
            fetchCouncilMeetingTypeOptions();
        }
    }, [token]);

    const onChangeEventHeading = (event) => {
        update("eventHeading", event.target.value);
    };

    const onChangeAllEventsLink = (link) => {
        update("allEventsLink", link);
    };

    const onChangeShowEventDescriptions = (event) => {
        update("showEventDescriptions", event.target.checked);
    };

    const onChangeCouncilMeetingHeading = (event) => {
        update("councilMeetingHeading", event.target.value);
    };

    const onChangeCouncilMeetingType = (event) => {
        update("councilMeetingType", event.target.value);
    };

    const onChangeAllCouncilMeetingsLink = (link) => {
        update("allCouncilMeetingsLink", link);
    };

    return (
        <div className='simplicity-editor-frame-events-council-feed'>
            <div className="simplicity-editor-frame-events-council-feed-top">
                <LabelledField label="Heading: Upcoming events">
                    <TextField
                        fullWidth
                        value={eventHeading}
                        onChange={onChangeEventHeading}
                        placeholder="Upcoming events"
                        variant="outlined"
                    />
                </LabelledField>
                <LabelledField label="Link: All events">
                    <EventsCouncilFeedLinkSelector
                        link={allEventsLink}
                        onLinkChange={onChangeAllEventsLink}
                        onLinkRemove={() => onChangeAllEventsLink(null)}
                    />
                </LabelledField>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showEventDescriptions}
                            onChange={onChangeShowEventDescriptions}
                        />
                    }
                    label="Show event descriptions"
                />
            </div>
            <div className="simplicity-editor-frame-events-council-feed-bottom">
                <div className="titlebar">Next council meeting</div>
                <LabelledField label="Heading: Council meeting">
                    <TextField
                        fullWidth
                        value={councilMeetingHeading}
                        onChange={onChangeCouncilMeetingHeading}
                        placeholder="Next council meeting"
                        variant="outlined"
                    />
                </LabelledField>
                <LabelledField label="Council meeting types" tooltip="Select council meeting type">
                    <FormControl fullWidth>
                        <Select
                            value={councilMeetingType}
                            onChange={onChangeCouncilMeetingType}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {councilMeetingTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </LabelledField>
                <LabelledField label="Link: All council meetings">
                    <EventsCouncilFeedLinkSelector
                        link={allCouncilMeetingsLink}
                        onLinkChange={onChangeAllCouncilMeetingsLink}
                        onLinkRemove={() => onChangeAllCouncilMeetingsLink(null)}
                    />
                </LabelledField>
            </div>
        </div>
    );
};

EventsCouncilFeedComponentEditor.displayName = "EventsCouncilFeedComponentEditor";
export { EventsCouncilFeedComponentEditor };

